import type { ReactNode } from 'react';
import { Button } from '@/shared/components/ui/button';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { TooltipProvider } from '@/shared/components/ui/tooltip';
import { useAdminAuth } from '../../../hooks/useAdminAuth';
import { REFERRAL_LIST_PAGE_SIZE, useReferralList } from '../../../hooks/useReferralList';
import type { ReferralListQueryParams, ReferralListRow } from '../../../types/engagement-metrics.types';
import { ErrorState } from '../../analytics/shared/ErrorState';
import { ReferralsFilters } from '../referrals/ReferralsFilters';
import { ReferralsTable } from '../referrals/ReferralsTable';
import { REFERRALS_TAB_SUBTITLE } from '../referrals/referralsCopy';

type EngagementMetricsReferralsTabProps = {
  isActive?: boolean;
  /**
   * Seam for slices 19/20: edit/delete buttons for a manual row. Rendered only
   * with MANAGE_USERS_AND_RESOURCES; the Actions column is hidden otherwise.
   */
  renderRowActions?: (row: ReferralListRow) => ReactNode;
  /**
   * Seam for header buttons, given the current params (no cursor): Export CSV
   * (slice 21) and "Register referral" (needs the modal's unlocked referred
   * party from slice 19; gate it on `canManage`).
   */
  renderHeaderActions?: (params: ReferralListQueryParams, canManage: boolean) => ReactNode;
};

/**
 * Referrals sub-tab (feature 117, variant A): read model list with search,
 * filters, sort, Previous/Next. Ignores the dashboard date bar.
 */
export const EngagementMetricsReferralsTab = ({
  isActive = true,
  renderRowActions,
  renderHeaderActions,
}: EngagementMetricsReferralsTabProps) => {
  const list = useReferralList(isActive);
  const { canManageUsersAndResources } = useAdminAuth();

  if (!isActive) return null;

  const first = list.pageIndex * REFERRAL_LIST_PAGE_SIZE + 1;
  const last = list.pageIndex * REFERRAL_LIST_PAGE_SIZE + list.rows.length;

  return (
    <TooltipProvider>
      <div className="space-y-4 pt-6" data-testid="engagement-metrics-referrals-tab">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="max-w-2xl space-y-1">
            <p className="text-sm text-gray-600">{REFERRALS_TAB_SUBTITLE}</p>
            <p className="text-sm font-semibold text-gray-900">
              {list.total} {list.total === 1 ? 'referral' : 'referrals'}
            </p>
          </div>
          <div className="flex gap-2">
            {renderHeaderActions?.(list.params, canManageUsersAndResources)}
          </div>
        </div>

        <ReferralsFilters filters={list.filters} onChange={list.updateFilters} onClear={list.clearFilters} />

        {list.isError ? (
          <ErrorState message="Failed to load referrals. Please try again." onRetry={() => list.refetch()} />
        ) : list.isLoading ? (
          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className={`h-12 w-full rounded-none ${i > 0 ? 'border-t border-gray-100' : ''}`} />
            ))}
          </div>
        ) : (
          <ReferralsTable
            rows={list.rows}
            sort={list.sort}
            onSort={list.toggleSort}
            renderRowActions={canManageUsersAndResources ? renderRowActions : undefined}
            footer={
              <div className="flex items-center justify-between border-t border-gray-100 px-4 py-3 text-sm text-gray-600">
                <span>
                  {list.rows.length === 0 ? 'No referrals' : `${first}–${last} of ${list.total}`} ·{' '}
                  {REFERRAL_LIST_PAGE_SIZE} per page
                </span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" disabled={!list.hasPrevious} onClick={list.goPrevious}>
                    Previous
                  </Button>
                  <Button variant="outline" size="sm" disabled={!list.hasNext} onClick={list.goNext}>
                    Next
                  </Button>
                </div>
              </div>
            }
          />
        )}
      </div>
    </TooltipProvider>
  );
};
